import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import DynamicFieldRenderer from './fields/DynamicFieldRenderer';
import { validateDocumentValues } from 'helpers/validateDocumentValues';
import evaluateConditions from 'helpers/evaluateConditions';
import {
  createDocument,
  updateDocument,
  getDocumentDetail,
  deleteDocumentFile
} from 'helpers/documentManagerApi';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'tiff', 'doc', 'docx'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const getExistingFieldFile = (value, fallbackFile) =>
  value?.existing_file || fallbackFile || null;

const getExistingFileModeValue = (value, fallbackFile) => {
  const existingFile = getExistingFieldFile(value, fallbackFile);

  if (!existingFile) {
    return { mode: 'file', file_index: null };
  }

  return {
    mode: 'file',
    file_index: null,
    has_existing_file: true,
    existing_file: existingFile
  };
};

const getSelectedFileModeValue = () => ({
  mode: 'file',
  file_index: null,
  has_existing_file: false
});

// Nested Sub-component to render an individual document section in batch creation mode
const DocumentSection = ({
  section,
  availableTypes,
  onChange,
  onRemove,
  canRemove,
  index
}) => {
  const { typeId, title, description, fieldValues, fieldFiles, files, errors } =
    section;

  const matchedType = availableTypes.find(t => t.id === Number(typeId)) || null;
  const fieldsDefs = matchedType ? matchedType.fields || [] : [];
  const fileMode = matchedType?.file_mode || 'multiple';
  const maxAllowedFiles = fileMode === 'single' ? 1 : 10;

  const visibleFields = fieldsDefs
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter(f => evaluateConditions(f.conditions, fieldValues));

  useEffect(() => {
    if (!matchedType) return;
    const visibleKeys = new Set(visibleFields.map(f => f.field_key));
    let changed = false;
    const cleaned = { ...fieldValues };
    Object.keys(cleaned).forEach(key => {
      if (!visibleKeys.has(key)) {
        delete cleaned[key];
        changed = true;
      }
    });
    if (changed) {
      onChange({ ...section, fieldValues: cleaned });
    }
  }, [fieldValues, matchedType, visibleFields]); // Avoid loop by checking changes

  const handleTypeSelect = selectedOption => {
    const newTypeId = selectedOption ? selectedOption.value : '';
    onChange({
      ...section,
      typeId: newTypeId,
      fieldValues: {},
      fieldFiles: {},
      errors: {},
      files: []
    });
  };

  const handleFieldValueChange = (fieldKey, value) => {
    const updatedValues = {
      ...fieldValues,
      [fieldKey]: value
    };
    const updatedErrors = { ...errors };
    delete updatedErrors[fieldKey];
    onChange({
      ...section,
      fieldValues: updatedValues,
      errors: updatedErrors
    });
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: acceptedFiles => {
      const validFiles = [];
      for (const file of acceptedFiles) {
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          toast.error(
            `File "${file.name}" has an unsupported format. Only PDF, JPG, JPEG, PNG, TIFF, DOC, DOCX are allowed.`
          );
          continue;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`File "${file.name}" exceeds the 10 MB limit.`);
          continue;
        }
        validFiles.push(file);
      }

      if (files.length + validFiles.length > maxAllowedFiles) {
        toast.warning(`Maximum of ${maxAllowedFiles} files allowed.`);
        return;
      }

      const updatedFiles =
        fileMode === 'single' ? validFiles : [...files, ...validFiles];
      onChange({
        ...section,
        files: updatedFiles
      });
    }
  });

  const handleRemoveFile = fileIdx => {
    onChange({
      ...section,
      files: files.filter((_, idx) => idx !== fileIdx)
    });
  };

  const typeOptions = availableTypes.map(t => ({
    value: t.id,
    label: t.name,
    file_mode: t.file_mode
  }));

  const selectedTypeOption =
    typeOptions.find(opt => Number(opt.value) === Number(typeId)) || null;

  return (
    <Card className="mb-4 border bg-card">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
        <h6 className="mb-0 text-700 fs-10">Document #{index + 1}</h6>
        {canRemove && (
          <Button
            variant="link"
            className="text-danger p-0 border-0 fs-10"
            onClick={onRemove}
            title="Remove this document"
          >
            <FontAwesomeIcon icon="trash" />
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semi-bold">Document Type</Form.Label>
              <ThemedSelect
                options={typeOptions}
                value={selectedTypeOption}
                onChange={handleTypeSelect}
                placeholder="Select type..."
                classNamePrefix="react-select"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semi-bold">
                Document Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={e => onChange({ ...section, title: e.target.value })}
                placeholder="e.g. Lab Results"
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semi-bold">Description / Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={description}
            onChange={e =>
              onChange({ ...section, description: e.target.value })
            }
            placeholder="Optional notes"
          />
        </Form.Group>

        {matchedType && visibleFields.length > 0 && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="mb-3 text-700 fs-11">Metadata Parameters</h6>
            <Row>
              {visibleFields.map(field => (
                <Col key={field.field_key} md={6}>
                  <DynamicFieldRenderer
                    field={field}
                    value={fieldValues[field.field_key]}
                    onChange={val =>
                      handleFieldValueChange(field.field_key, val)
                    }
                    onFileSelect={(key, file) => {
                      onChange({
                        ...section,
                        fieldFiles: { ...fieldFiles, [key]: file },
                        fieldValues: {
                          ...fieldValues,
                          [key]: getSelectedFileModeValue()
                        }
                      });
                    }}
                    error={errors[field.field_key]}
                  />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {matchedType && (
          <div>
            <Form.Label className="fw-semi-bold">
              Attachments <span className="text-danger">*</span>
            </Form.Label>
            <div
              {...getRootProps()}
              className="border border-dashed rounded p-4 text-center bg-card mb-3 cursor-pointer"
            >
              <input {...getInputProps()} />
              <FontAwesomeIcon
                icon="file-upload"
                className="fs-3 mb-2 text-primary"
              />
              {isDragActive ? (
                <p className="mb-0 text-primary fs-9">
                  Drop the files here ...
                </p>
              ) : (
                <div>
                  <p className="mb-1 text-700 fs-9">
                    Drag & drop files here, or{' '}
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 border-0 fs-9 align-baseline"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        open();
                      }}
                    >
                      browse
                    </Button>
                  </p>
                  <p className="text-400 mb-0 fs-11">
                    Allowed formats: PDF, JPG, JPEG, PNG, TIFF, DOC, DOCX. Mode:{' '}
                    <span className="text-capitalize fw-bold">{fileMode}</span>.
                  </p>
                </div>
              )}
            </div>

            {files.length > 0 && (
              <ListGroup className="fs-10">
                {files.map((file, fileIdx) => (
                  <ListGroup.Item
                    key={fileIdx}
                    className="d-flex justify-content-between align-items-center py-2"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={['far', 'file']}
                        className="text-primary"
                      />
                      <span className="fw-semi-bold">{file.name}</span>
                      <span className="text-500 font-sans-serif fs-11">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button
                      variant="link"
                      className="text-danger p-0 border-0"
                      onClick={() => handleRemoveFile(fileIdx)}
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

DocumentSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.number.isRequired,
    typeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    fieldValues: PropTypes.object,
    fieldFiles: PropTypes.object,
    files: PropTypes.array,
    errors: PropTypes.object
  }).isRequired,
  availableTypes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired
};

const DocumentForm = ({
  patientId,
  documentId,
  availableTypes,
  onCancel,
  onSuccess
}) => {
  const isEditMode = !!documentId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit-mode specific states
  const [editType, setEditType] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFieldValues, setEditFieldValues] = useState({});
  const [editFieldFiles, setEditFieldFiles] = useState({});
  const [editExistingFieldFiles, setEditExistingFieldFiles] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [schemaChanged, setSchemaChanged] = useState(false);
  const [editFieldsDefs, setEditFieldsDefs] = useState([]);

  // Batch-create specific states (Option A)
  const [sections, setSections] = useState([
    {
      id: Date.now(),
      typeId: '',
      title: '',
      description: '',
      fieldValues: {},
      fieldFiles: {},
      files: [],
      errors: {}
    }
  ]);

  // Load document details in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchDetail = async () => {
        try {
          setLoading(true);
          const res = await getDocumentDetail(documentId);
          if (res && res.status) {
            const doc = res.data;
            const existingFieldFiles = { ...(doc.field_files || {}) };
            const hydratedFieldValues = { ...(doc.field_values || {}) };
            Object.entries(hydratedFieldValues).forEach(([fieldKey, value]) => {
              if (value?.mode === 'file' && value?.existing_file) {
                existingFieldFiles[fieldKey] = value.existing_file;
              }
            });
            Object.keys(existingFieldFiles).forEach(fieldKey => {
              const value = hydratedFieldValues[fieldKey];
              if (
                value === undefined ||
                value === null ||
                value === '' ||
                value?.mode === 'file'
              ) {
                hydratedFieldValues[fieldKey] = getExistingFileModeValue(
                  value,
                  existingFieldFiles[fieldKey]
                );
              }
            });
            setEditTitle(doc.title || '');
            setEditDescription(doc.description || '');
            setEditFieldValues(hydratedFieldValues);
            setEditExistingFieldFiles(existingFieldFiles);
            setEditFieldFiles({});
            setExistingFiles(doc.files || []);
            setSchemaChanged(!!doc.schema_changed);
            setEditFieldsDefs(doc.fields || []);

            const matchedType = availableTypes.find(
              t => t.id === doc.document_type_id
            );
            if (matchedType) {
              setEditType({
                value: matchedType.id,
                label: matchedType.name,
                file_mode: matchedType.file_mode
              });
            } else {
              setEditType({
                value: doc.document_type_id,
                label: doc.name,
                file_mode: doc.file_mode || 'multiple'
              });
            }
          } else {
            toast.error(res?.message || 'Failed to fetch document details');
            onCancel();
          }
        } catch (err) {
          console.error(err);
          toast.error('Error fetching document details');
          onCancel();
        } finally {
          setLoading(false);
        }
      };

      fetchDetail();
    }
  }, [documentId, isEditMode, availableTypes, onCancel]);

  // Edit Mode dynamic validation and handlers
  const visibleEditFields = editFieldsDefs
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter(f => evaluateConditions(f.conditions, editFieldValues));

  useEffect(() => {
    if (!isEditMode || !editFieldsDefs.length) return;
    const visibleKeys = new Set(visibleEditFields.map(f => f.field_key));
    let changed = false;
    const cleaned = { ...editFieldValues };
    Object.keys(cleaned).forEach(key => {
      if (!visibleKeys.has(key)) {
        delete cleaned[key];
        changed = true;
      }
    });
    if (changed) {
      setEditFieldValues(cleaned);
    }
  }, [editFieldValues, editFieldsDefs, visibleEditFields, isEditMode]);

  const handleEditFieldValueChange = (fieldKey, value) => {
    setEditFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
    if (editErrors[fieldKey]) {
      setEditErrors(prev => {
        const copy = { ...prev };
        delete copy[fieldKey];
        return copy;
      });
    }
  };

  const handleAddSection = () => {
    setSections(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        typeId: '',
        title: '',
        description: '',
        fieldValues: {},
        fieldFiles: {},
        files: [],
        errors: {}
      }
    ]);
  };

  const handleRemoveSection = sectionId => {
    setSections(prev => prev.filter(sec => sec.id !== sectionId));
  };

  const handleSectionChange = (sectionId, updatedData) => {
    setSections(prev =>
      prev.map(sec => (sec.id === sectionId ? { ...sec, ...updatedData } : sec))
    );
  };

  // Dropzone setup for Edit Mode
  const editFileMode = editType?.file_mode || 'multiple';
  const editMaxAllowedFiles = editFileMode === 'single' ? 1 : 10;
  const editCurrentTotalFiles = existingFiles.length + newFiles.length;

  const editDropzone = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: acceptedFiles => {
      const validFiles = [];
      for (const file of acceptedFiles) {
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          toast.error(
            `File "${file.name}" has an unsupported format. Only PDF, JPG, JPEG, PNG, TIFF, DOC, DOCX are allowed.`
          );
          continue;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`File "${file.name}" exceeds the 10 MB limit.`);
          continue;
        }
        validFiles.push(file);
      }

      if (editCurrentTotalFiles + validFiles.length > editMaxAllowedFiles) {
        toast.warning(`Maximum of ${editMaxAllowedFiles} files allowed.`);
        return;
      }

      if (editFileMode === 'single') {
        setNewFiles(validFiles);
      } else {
        setNewFiles(prev => [...prev, ...validFiles]);
      }
    }
  });

  const openEditFile = editDropzone.open;

  const handleRemoveEditNewFile = index => {
    setNewFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleDeleteExistingFile = async (fileId, fileName) => {
    if (
      window.confirm(
        `Are you sure you want to delete file "${fileName}"? This will take effect immediately.`
      )
    ) {
      try {
        const res = await deleteDocumentFile(documentId, fileId);
        if (res && res.status) {
          toast.success('File removed successfully');
          setExistingFiles(prev => prev.filter(f => f.id !== fileId));
        } else {
          toast.error(res?.message || 'Failed to remove file');
        }
      } catch (err) {
        console.error(err);
        toast.error('Error removing file');
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (isEditMode) {
      if (!editTitle.trim()) {
        toast.error('Document Title is required.');
        return;
      }

      const { valid, errors: validationErrors } = validateDocumentValues(
        editFieldsDefs,
        editFieldValues,
        { ...editExistingFieldFiles, ...editFieldFiles }
      );
      if (!valid) {
        setEditErrors(validationErrors);
        toast.error('Please fix validation errors before submitting.');
        return;
      }

      if (editCurrentTotalFiles === 0) {
        toast.error('At least one file attachment is required.');
        return;
      }

      try {
        setSaving(true);
        const fieldFileEntries = Object.entries(editFieldFiles).filter(
          ([fieldKey, file]) =>
            file && editFieldValues[fieldKey]?.mode === 'file'
        );
        const finalEditFieldValues = { ...editFieldValues };
        const fieldFileIndexes = {};

        fieldFileEntries.forEach(([fieldKey], index) => {
          const fileIndex = newFiles.length + index;
          fieldFileIndexes[fieldKey] = fileIndex;
          finalEditFieldValues[fieldKey] = {
            mode: 'file',
            file_index: fileIndex
          };
        });

        const updateForm = new FormData();
        updateForm.append('title', editTitle);
        updateForm.append('description', editDescription);
        updateForm.append('field_values', JSON.stringify(finalEditFieldValues));
        newFiles.forEach(file => {
          updateForm.append('documents', file);
        });
        fieldFileEntries.forEach(([, file]) => {
          updateForm.append('documents', file);
        });
        if (fieldFileEntries.length > 0) {
          updateForm.append(
            'field_file_indexes',
            JSON.stringify(fieldFileIndexes)
          );
        }

        const updateRes = await updateDocument(documentId, updateForm);
        if (!updateRes || !updateRes.status) {
          toast.error(updateRes?.message || 'Failed to update document');
          setSaving(false);
          return;
        }

        toast.success('Document updated successfully');
        onSuccess();
      } catch (err) {
        console.error(err);
        toast.error('Error saving document updates');
      } finally {
        setSaving(false);
      }
    } else {
      // Option A: Batch Create Mode
      let allValid = true;
      const updatedSections = sections.map((sec, idx) => {
        const matchedType = availableTypes.find(
          t => t.id === Number(sec.typeId)
        );
        const fields = matchedType ? matchedType.fields || [] : [];
        const { valid, errors: validationErrors } = validateDocumentValues(
          fields,
          sec.fieldValues,
          sec.fieldFiles
        );

        if (!sec.typeId) {
          toast.error(
            `Please select a Document Type for Document #${idx + 1}.`
          );
          allValid = false;
        }
        if (!sec.title.trim()) {
          toast.error(`Document Title is required for Document #${idx + 1}.`);
          allValid = false;
        }
        if (sec.files.length === 0) {
          toast.error(
            `Please attach at least one file for Document #${idx + 1} ("${
              sec.title || 'Untitled'
            }").`
          );
          allValid = false;
        }

        return {
          ...sec,
          errors: valid ? {} : validationErrors
        };
      });

      if (!allValid) {
        setSections(updatedSections);
        return;
      }

      // Format Option A Payload
      try {
        setSaving(true);

        const documentsJson = sections.map(sec => ({
          document_type_id: Number(sec.typeId),
          title: sec.title,
          description: sec.description,
          field_values: sec.fieldValues,
          file_indexes: [],
          field_file_indexes: {}
        }));

        const allFiles = [];
        sections.forEach((sec, secIdx) => {
          const startIdx = allFiles.length;
          sec.files.forEach(f => {
            allFiles.push(f);
          });
          const endIdx = allFiles.length;
          const fileIndexes = [];
          for (let i = startIdx; i < endIdx; i++) {
            fileIndexes.push(i);
          }
          documentsJson[secIdx].file_indexes = fileIndexes;

          // Process field-level files
          const finalFieldValues = { ...sec.fieldValues };
          const field_file_indexes = {};

          if (sec.fieldFiles) {
            Object.entries(sec.fieldFiles).forEach(([fieldKey, file]) => {
              const val = finalFieldValues[fieldKey];
              if (val?.mode === 'file') {
                const idx = allFiles.length;
                allFiles.push(file);
                field_file_indexes[fieldKey] = idx;
                finalFieldValues[fieldKey] = { mode: 'file', file_index: idx };
              }
            });
          }
          documentsJson[secIdx].field_values = finalFieldValues;
          documentsJson[secIdx].field_file_indexes = field_file_indexes;
        });

        const createForm = new FormData();
        createForm.append('patient_id', patientId);
        createForm.append('documents_json', JSON.stringify(documentsJson));
        allFiles.forEach(file => {
          createForm.append('documents', file);
        });

        const createRes = await createDocument(createForm);
        if (createRes && createRes.status) {
          toast.success('Documents uploaded successfully');
          onSuccess();
        } else {
          toast.error(createRes?.message || 'Failed to upload documents');
        }
      } catch (err) {
        console.error(err);
        toast.error('Error saving documents');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center border-0 shadow-none">
        <Card.Body>
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-600 mb-0">Loading document details...</p>
        </Card.Body>
      </div>
    );
  }

  return (
    <div className="border-0">
      <Card.Header className="bg-light px-0 py-2 mb-3 border-0 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {isEditMode ? 'Edit Document' : 'Add New Documents'}
        </h5>
      </Card.Header>
      <Card.Body className="p-0">
        {isEditMode && schemaChanged && (
          <Alert variant="warning" className="fs-10 py-2 mb-3">
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            <strong>Notice:</strong> The document type schema has been updated
            since this document was created. Some fields might be new or
            re-ordered. Legacy fields not part of the active schema are hidden
            or read-only.
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {isEditMode ? (
            /* Single Edit Mode */
            <div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="editDocumentType">
                    <Form.Label className="fw-semi-bold">
                      Document Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editType?.label || ''}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="editDocumentTitle">
                    <Form.Label className="fw-semi-bold">
                      Document Title <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      placeholder="e.g. Lab Report"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="editDocumentDesc">
                <Form.Label className="fw-semi-bold">
                  Description / Notes
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  placeholder="Optional notes"
                />
              </Form.Group>

              {visibleEditFields.length > 0 && (
                <div className="border rounded p-3 mb-4 bg-light">
                  <h6 className="mb-3 text-700 fs-10">
                    Document Metadata Parameters
                  </h6>
                  <Row>
                    {visibleEditFields.map(field => (
                      <Col key={field.field_key} md={6}>
                        <DynamicFieldRenderer
                          field={field}
                          value={editFieldValues[field.field_key]}
                          onChange={val =>
                            handleEditFieldValueChange(field.field_key, val)
                          }
                          existingFile={editExistingFieldFiles[field.field_key]}
                          onFileSelect={(key, file) => {
                            setEditFieldFiles(prev => ({
                              ...prev,
                              [key]: file
                            }));
                            setEditFieldValues(prev => ({
                              ...prev,
                              [key]: getSelectedFileModeValue()
                            }));
                          }}
                          error={editErrors[field.field_key]}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              <div className="mb-4">
                <Form.Label className="fw-semi-bold">
                  File Attachments <span className="text-danger">*</span>
                </Form.Label>
                <div
                  {...editDropzone.getRootProps()}
                  className="border border-dashed rounded p-4 text-center bg-card mb-3 cursor-pointer"
                >
                  <input {...editDropzone.getInputProps()} />
                  <FontAwesomeIcon
                    icon="file-upload"
                    className="fs-3 mb-2 text-primary"
                  />
                  {editDropzone.isDragActive ? (
                    <p className="mb-0 text-primary fs-9">
                      Drop the files here ...
                    </p>
                  ) : (
                    <div>
                      <p className="mb-1 text-700 fs-9">
                        Drag & drop files here, or{' '}
                        <Button
                          type="button"
                          variant="link"
                          className="p-0 border-0 fs-9 align-baseline"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            openEditFile();
                          }}
                        >
                          browse
                        </Button>
                      </p>
                      <p className="text-400 mb-0 fs-11">
                        Allowed types: PDF, JPG, JPEG, PNG, TIFF, DOC, DOCX (Max
                        10 MB per file).
                      </p>
                      <p className="text-400 mb-0 fs-11">
                        Mode:{' '}
                        <span className="text-capitalize fw-bold">
                          {editFileMode}
                        </span>
                        .
                      </p>
                    </div>
                  )}
                </div>

                {existingFiles.length > 0 && (
                  <div className="mb-3">
                    <h6 className="fs-10 text-600 mb-2">
                      Existing Attachments
                    </h6>
                    <ListGroup className="fs-10">
                      {existingFiles.map(file => (
                        <ListGroup.Item
                          key={file.id}
                          className="d-flex justify-content-between align-items-center py-2 bg-light"
                        >
                          <a
                            href={file.preview_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-flex align-items-center gap-2 text-decoration-none text-900"
                          >
                            <FontAwesomeIcon
                              icon={['far', 'file-pdf']}
                              className="text-danger"
                            />
                            <span className="fw-semi-bold">
                              {file.filename}
                            </span>
                            <span className="text-500 font-sans-serif fs-11">
                              ({(file.file_size / 1024).toFixed(1)} KB)
                            </span>
                          </a>
                          <Button
                            variant="link"
                            className="text-danger p-0 border-0"
                            onClick={() =>
                              handleDeleteExistingFile(file.id, file.filename)
                            }
                          >
                            <FontAwesomeIcon icon="times" />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {newFiles.length > 0 && (
                  <div>
                    <h6 className="fs-10 text-600 mb-2">
                      New Attachments to Upload
                    </h6>
                    <ListGroup className="fs-10">
                      {newFiles.map((file, idx) => (
                        <ListGroup.Item
                          key={idx}
                          className="d-flex justify-content-between align-items-center py-2"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon
                              icon={['far', 'file']}
                              className="text-primary"
                            />
                            <span className="fw-semi-bold">{file.name}</span>
                            <span className="text-500 font-sans-serif fs-11">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            variant="link"
                            className="text-danger p-0 border-0"
                            onClick={() => handleRemoveEditNewFile(idx)}
                          >
                            <FontAwesomeIcon icon="times" />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Option A: Dynamic Batch Create Mode */
            <div>
              {sections.map((section, idx) => (
                <DocumentSection
                  key={section.id}
                  section={section}
                  availableTypes={availableTypes}
                  onChange={updatedSec =>
                    handleSectionChange(section.id, updatedSec)
                  }
                  onRemove={() => handleRemoveSection(section.id)}
                  canRemove={sections.length > 1}
                  index={idx}
                />
              ))}

              <Button
                variant="outline-primary"
                size="sm"
                className="mb-4 d-block w-100 py-2 fs-10"
                onClick={handleAddSection}
              >
                <FontAwesomeIcon icon="plus" className="me-2" />
                Add Another Document Type Section
              </Button>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={saving}>
              {saving ? 'Saving...' : 'Save Documents'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </div>
  );
};

DocumentForm.propTypes = {
  patientId: PropTypes.number.isRequired,
  facilityId: PropTypes.number,
  documentId: PropTypes.number,
  availableTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      file_mode: PropTypes.string.isRequired,
      fields: PropTypes.array
    })
  ).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default DocumentForm;
